import {
    BinaryExpr,
    ComparisonExpr,
    ConcatExpr,
    InvokeFunctionExpr,
} from '@mintdata-wsp/common/controllers/spreadsheet/formula/ast/FormulaASTNodes';
import {
    FunctionNameLiteral,
    IFormulaNode,
} from '@mintdata-wsp/common/controllers/spreadsheet/formula/ast/FormulaDefinitions';
import {FormulaValueCoercionUtils} from '@mintdata-wsp/common/controllers/spreadsheet/formula/FormulaValueCoercionUtils';
import {ArrayFormulaFunctionInvoker} from '@mintdata-wsp/common/controllers/spreadsheet/formula/invoke/ArrayFormulaFunctionInvoker';
import {FunctionUtil} from '@mintdata-wsp/common/controllers/spreadsheet/formula/invoke/FunctionUtil';
import {EvalLogic} from '@mintdata-wsp/common/controllers/spreadsheet/formula/operations/eval/EvalLogic';
import {ISheetValue} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {area, mk} from '@mintdata/mint-kit/api/log/mk';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {AEmpty} from '@mintdata/mint-kit/api/values/AEmpty';
import {EvalErrors} from '@mintdata/mint-kit/api/values/errors/EvalErrors';
import {ColumnOffset, FrameData, IFrame, RowIndex} from '@mintdata/mint-kit/api/values/frames/Frame';
import {FrameField} from '@mintdata/mint-kit/api/values/frames/FrameField';
import {Frames} from '@mintdata/mint-kit/api/values/frames/Frames';

export class ArrayFormulaEvalLogic extends EvalLogic
{
    public invokeFunction(invokeFunc:InvokeFunctionExpr, args:IFormulaNode[], argValues:IValue[]):Promise<IValue>
    {
        const literal:FunctionNameLiteral = FunctionUtil.parseFunctionName(invokeFunc.functionFullName.value);

        mk.debug(area.Formula, `[toValue] invokeFunction: ${literal.toString()}`);

        return ArrayFormulaFunctionInvoker.invokeFunction(
            this._context,
            this._functionResolver,
            EvalLogic.getNamingContext(),
            literal.namespace,
            literal.name,
            args,
            argValues,
            this._javaEvalContext
        );
    }

    public binary(lhsValue:IValue | ISheetValue, rhsValue:IValue | ISheetValue, binaryOp:BinaryExpr):IValue
    {
        const lValue:IValue = FormulaValueCoercionUtils.ConvertToIValue(
            this._context.getSheetContext(),
            EvalLogic.getNamingContext(),
            lhsValue
        ) as IValue;
        const rValue:IValue = FormulaValueCoercionUtils.ConvertToIValue(
            this._context.getSheetContext(),
            EvalLogic.getNamingContext(),
            rhsValue
        ) as IValue;

        const opType = binaryOp.opType;

        if( !lValue )
        {
            mk.debug(area.Formula, `[toValue] ArrayFormula binaryExpr: ${binaryOp.toString()} => ${lhsValue}, ${rhsValue}, ${opType}`);
            return EvalErrors.Value;
        }

        if( lValue.isError )
        {
            mk.debug(area.Formula, `[toValue] ArrayFormula binaryExpr: ${binaryOp.toString()} => ${lhsValue}, ${rhsValue}, ${opType}`);
            return lValue;
        }

        if( !rValue )
        {
            mk.debug(area.Formula, `[toValue] ArrayFormula binaryExpr: ${binaryOp.toString()} => ${lhsValue}, ${rhsValue}, ${opType}`);
            return EvalErrors.Value;
        }

        if( rValue.isError )
        {
            mk.debug(area.Formula, `[toValue] ArrayFormula binaryExpr: ${binaryOp.toString()} => ${lhsValue}, ${rhsValue}, ${opType}`);
            return rValue;
        }

        const array = this._processArray(lValue, rValue, Type.Number, (leftValue, rightValue) => super.binary(leftValue, rightValue, binaryOp));

        if( array )
            return array;

        return super.binary(lhsValue, rhsValue, binaryOp);
    }

    public comparison(lhsValue:IValue | ISheetValue, rhsValue:IValue | ISheetValue, comparison:ComparisonExpr):IValue
    {
        const lValue:IValue = FormulaValueCoercionUtils.ConvertToIValue(
            this._context.getSheetContext(),
            EvalLogic.getNamingContext(),
            lhsValue
        ) as IValue;
        const rValue:IValue = FormulaValueCoercionUtils.ConvertToIValue(
            this._context.getSheetContext(),
            EvalLogic.getNamingContext(),
            rhsValue
        ) as IValue;

        const opType = comparison.opType;

        if( !lValue )
        {
            mk.debug(area.Formula, `[toValue] comparisonExpr: ${comparison.toString()} => ${lhsValue}, ${rhsValue}, ${opType}`);
            return EvalErrors.Value;
        }

        if( lValue.isError )
        {
            mk.debug(area.Formula, `[toValue] comparisonExpr: ${comparison.toString()} => ${lhsValue}, ${rhsValue}, ${opType}`);
            return lValue;
        }

        if( !rValue )
        {
            mk.debug(area.Formula, `[toValue] comparisonExpr: ${comparison.toString()} => ${lhsValue}, ${rhsValue}, ${opType}`);
            return EvalErrors.Value;
        }

        if( rValue.isError )
        {
            mk.debug(area.Formula, `[toValue] comparisonExpr: ${comparison.toString()} => ${lhsValue}, ${rhsValue}, ${opType}`);
            return rValue;
        }

        const array = this._processArray(lValue, rValue, Type.Boolean, (leftValue, rightValue) => super.comparison(leftValue, rightValue, comparison));

        if( array )
            return array;

        return super.comparison(lhsValue, rhsValue, comparison);
    }

    public concat(lhsValue:IValue | ISheetValue, rhsValue:IValue | ISheetValue, concatOp:ConcatExpr):IValue
    {
        const lValue:IValue = FormulaValueCoercionUtils.ConvertToIValue(
            this._context.getSheetContext(),
            EvalLogic.getNamingContext(),
            lhsValue
        ) as IValue;
        const rValue:IValue = FormulaValueCoercionUtils.ConvertToIValue(
            this._context.getSheetContext(),
            EvalLogic.getNamingContext(),
            rhsValue
        ) as IValue;

        if( !lValue )
        {
            mk.debug(area.Formula, `[toValue] concatExpr: ${concatOp.toString()} => ${lhsValue}, ${rhsValue}`);
            return EvalErrors.Value;
        }

        if( lValue.isError )
        {
            mk.debug(area.Formula, `[toValue] concatExpr: ${concatOp.toString()} => ${lhsValue}, ${rhsValue}`);
            return lValue;
        }

        if( !rValue )
        {
            mk.debug(area.Formula, `[toValue] concatExpr: ${concatOp.toString()} => ${lhsValue}, ${rhsValue}`);
            return EvalErrors.Value;
        }

        if( rValue.isError )
        {
            mk.debug(area.Formula, `[toValue] concatExpr: ${concatOp.toString()} => ${lhsValue}, ${rhsValue}`);
            return rValue;
        }

        const array = this._processArray(lValue, rValue, Type.String, (leftValue, rightValue) => super.concat(leftValue, rightValue, concatOp));

        if( array )
            return array;

        return super.concat(lhsValue, rhsValue, concatOp);
    }

    private _processArray(lValue:IValue, rValue:IValue, resultType:Type, calculateScalarValue:(leftValue:IValue, rightValue:IValue) => IValue):IValue | null
    {
        if( lValue.isFrame )
        {
            if( rValue.isFrame )
            {
                // left and right are frames

                const lhsAsFrame:IFrame = <IFrame>lValue;
                const rhsAsFrame:IFrame = <IFrame>rValue;

                const recordsCount = Math.max(lhsAsFrame.recordCount, rhsAsFrame.recordCount);
                const fieldsCount = Math.max(lhsAsFrame.fieldCount, rhsAsFrame.fieldCount);

                return ArrayFormulaEvalLogic.getFrameResult(recordsCount, fieldsCount, (fieldIndex, recordIndex) =>
                {
                    const leftValue = ArrayFormulaEvalLogic.getCellValue(lhsAsFrame, fieldIndex, recordIndex);
                    const rightValue = ArrayFormulaEvalLogic.getCellValue(rhsAsFrame, fieldIndex, recordIndex);
                    return calculateScalarValue(leftValue, rightValue);
                }, resultType);
            }
            else
            {
                // only left is frame
                const lhsAsFrame:IFrame = <IFrame>lValue;

                const recordsCount = lhsAsFrame.recordCount;
                const fieldsCount = lhsAsFrame.fieldCount;

                return ArrayFormulaEvalLogic.getFrameResult(recordsCount, fieldsCount, (fieldIndex, recordIndex) =>
                {
                    const leftValue = ArrayFormulaEvalLogic.getCellValue(lhsAsFrame, fieldIndex, recordIndex);
                    return calculateScalarValue(leftValue, rValue);
                }, resultType);
            }
        }
        else
        {
            if( rValue.isFrame )
            {
                // only right is frame

                const rhsAsFrame:IFrame = <IFrame>rValue;

                const recordsCount = rhsAsFrame.recordCount;
                const fieldsCount = rhsAsFrame.fieldCount;

                return ArrayFormulaEvalLogic.getFrameResult(recordsCount, fieldsCount, (fieldIndex, recordIndex) =>
                {
                    const rightValue = ArrayFormulaEvalLogic.getCellValue(rhsAsFrame, fieldIndex, recordIndex);
                    return calculateScalarValue(lValue, rightValue);
                }, resultType);
            }
            else
            {
                // left and right are not frames
                return null;
            }
        }
    }

    static getFrameResult(recordsCount:number, fieldsCount:number, getCellValue:(fieldIndex:ColumnOffset, recordIndex:RowIndex) => IValue, resultType:Type):IFrame
    {
        mk.debug(area.Formula, `[toValue] getFrameResult recordsCount:${recordsCount}, fieldsCount:${fieldsCount}`);

        const namingContext = EvalLogic.getNamingContext();

        const fields:Array<FrameField> = [];
        const data:FrameData = [];

        for( let recordIndex = 0; recordIndex < recordsCount; recordIndex++ )
        {
            const recordData:IValue[] = [];

            for( let fieldIndex = 0; fieldIndex < fieldsCount; fieldIndex++ )
            {
                if( fields.length < fieldsCount )
                    fields.push(new FrameField(namingContext.getNextColumnName(), resultType));

                const result = getCellValue(fieldIndex, recordIndex);
                //mk.debug(area.Formula, `[toValue] getFrameResult getCellValue(${fieldIndex}, ${recordIndex}) => ${result}`);

                recordData.push(result);
            }

            data.push(recordData);
        }

        return Frames.fromFieldsAndData(fields, data);
    }

    static getCellValue(frame:IFrame, fieldIndex:ColumnOffset, recordIndex:RowIndex):IValue
    {
        const cellByOffset = frame.getCellByOffset(fieldIndex, recordIndex);

        if( cellByOffset )
            return cellByOffset;

        return AEmpty.Value;
    }
}
